import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../lib/firebase';
import { doc, onSnapshot, collection, query, where, orderBy, updateDoc, limit } from 'firebase/firestore';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger 
} from "./dropdown-menu";
import { 
  User, UserCircle, Shield, LogOut, MapPin, Search, 
  History as HistoryIcon, ChevronLeft, ArrowRightLeft, 
  Briefcase, Power, Bell, BellOff, MessageSquare, CheckCircle, AlertTriangle, ShieldAlert, Zap, Timer, Megaphone, Clock
} from 'lucide-react'; // BellOff ADICIONADO AQUI
import { toast } from 'sonner';

import logoImg from '../../assets/logo-semfrase.png';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  
  const [liveProfile, setLiveProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user?.uid) return;

    const unsubProfile = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
      if (docSnap.exists()) setLiveProfile(docSnap.data());
    });

    const qNotify = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubNotify = onSnapshot(qNotify, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotifications(list);
      setUnreadCount(list.filter(n => !n.read).length);
    });

    return () => { unsubProfile(); unsubNotify(); };
  }, [user]);

  const userRole = (liveProfile?.userType || '').toLowerCase();
  const isAdmin = userRole === 'admin';
  const isProfessionalUser = userRole === 'technician';
  const isRejected = liveProfile?.technicianStatus === 'rejected';
  
  const isAtProfessionalPanel = location.pathname.includes('technician-dashboard') || location.pathname.includes('profile-tech');
  const hasProfessionalAccess = isProfessionalUser || isAdmin;
  const showBackButton = location.pathname !== '/dashboard' && location.pathname !== '/technician-dashboard' && location.pathname !== '/admin-dashboard';

  const displayCity = useMemo(() => {
    if (isAtProfessionalPanel) {
      return liveProfile?.professionalData?.address?.city || "Sua Região";
    }
    return liveProfile?.addressDetails?.city || "Sua Região";
  }, [liveProfile, isAtProfessionalPanel]);

  const getNotificationIcon = (category) => {
    switch (category) {
      case 'order': return <Zap size={14} />;
      case 'chat': return <MessageSquare size={14} />;
      case 'support': return <Megaphone size={14} />;
      case 'punishment': return <ShieldAlert size={14} />;
      case 'reminder': return <Timer size={14} />;
      default: return <Bell size={14} />;
    }
  };

  const handleMarkAsRead = async (notif) => {
    if (!notif.read) {
      await updateDoc(doc(db, 'notifications', notif.id), { read: true });
    }
    if (notif.link) navigate(notif.link);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/'; 
    } catch (error) { toast.error("Erro ao sair."); }
  };

  if (!user || !liveProfile) return null;

  return (
    <header className={`fixed top-0 left-0 right-0 z-[100] border-b py-3 md:py-4 px-4 md:px-8 shadow-md backdrop-blur-sm transition-all duration-300 ${isAtProfessionalPanel ? 'bg-orange-50/90 border-orange-100' : 'bg-white/95 border-slate-200'}`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 text-left">
        <div className="flex items-center gap-3 md:gap-4 shrink-0 text-left">
          {showBackButton && (
            <button onClick={() => navigate(-1)} className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-blue-600 hover:text-white transition-all active:scale-90">
              <ChevronLeft size={20} />
            </button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="relative cursor-pointer hover:scale-105 transition-transform">
                {isRejected && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center border-2 border-white z-20 animate-pulse shadow-lg text-white">
                    <ShieldAlert size={10} />
                  </div>
                )}
                {(isAtProfessionalPanel && liveProfile.professionalData?.photoUrl) || liveProfile.photoURL ? (
                  <img src={isAtProfessionalPanel && liveProfile.professionalData?.photoUrl ? liveProfile.professionalData.photoUrl : liveProfile.photoURL} alt="" className={`w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 shadow-md ${isRejected ? 'border-red-600' : isAtProfessionalPanel ? 'border-orange-500' : 'border-blue-600'}`} />
                ) : (
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full bg-slate-100 flex items-center justify-center border-2 shadow-md ${isRejected ? 'border-red-600' : isAtProfessionalPanel ? 'border-orange-500' : 'border-blue-600'}`}>
                    <User className={`w-5 h-5 ${isAtProfessionalPanel ? 'text-orange-500' : 'text-blue-600'}`} />
                  </div>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72 bg-white p-3 shadow-2xl border-slate-200 rounded-2xl z-[200] text-left" align="start">
               <DropdownMenuLabel className="px-3 py-4 font-black text-slate-900 uppercase italic text-[10px] border-b mb-2 tracking-widest text-center">Configurações</DropdownMenuLabel>
               {isAdmin && (
                 <DropdownMenuItem onClick={() => navigate('/admin-dashboard')} className="p-3 rounded-xl bg-red-50 text-red-600 font-bold flex items-center gap-3 uppercase text-[10px] mb-2 italic cursor-pointer hover:bg-red-100">
                    <Shield size={14} /> Painel Administrativo
                 </DropdownMenuItem>
               )}
               {isAtProfessionalPanel ? (
                 <DropdownMenuItem onClick={() => navigate('/profile-tech')} className="p-3 rounded-xl font-bold flex items-center gap-3 uppercase text-[10px] italic cursor-pointer hover:bg-orange-50 text-orange-600">
                    <Briefcase className="w-4 h-4" /> Editar Perfil Profissional
                 </DropdownMenuItem>
               ) : (
                 <>
                   <DropdownMenuItem onClick={() => navigate('/profile')} className="p-3 rounded-xl font-bold flex items-center gap-3 uppercase text-[10px] italic cursor-pointer hover:bg-blue-50 text-blue-600">
                      <UserCircle className="w-4 h-4" /> Editar Perfil Pessoal
                   </DropdownMenuItem>
                   <DropdownMenuSeparator className="my-2" />
                   <DropdownMenuItem onClick={() => navigate('/dashboard')} className="p-3 rounded-xl font-bold flex items-center gap-3 uppercase text-[10px] italic cursor-pointer hover:bg-slate-50 text-slate-700">
                      <Search className="w-4 h-4" /> Procurar Profissionais
                   </DropdownMenuItem>
                   <DropdownMenuItem onClick={() => navigate('/history')} className="p-3 rounded-xl font-bold flex items-center gap-3 uppercase text-[10px] italic cursor-pointer hover:bg-slate-50 text-slate-700">
                      <HistoryIcon className="w-4 h-4" /> Histórico de Pedidos
                   </DropdownMenuItem>
                 </>
               )}
               <DropdownMenuSeparator />
               <DropdownMenuItem onClick={handleSignOut} className="p-3 text-red-500 font-black flex items-center gap-3 uppercase text-[10px] italic cursor-pointer hover:bg-red-50">
                  <LogOut className="w-4 h-4" /> Encerrar Sessão
               </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-4">
              <div className="flex flex-col leading-none text-left justify-center">
                <div className="flex items-center gap-2 text-left">
                    <span className={`text-sm md:text-lg font-black tracking-tighter italic uppercase leading-none ${isAtProfessionalPanel ? 'text-orange-600' : 'text-blue-900'}`}>
                      {liveProfile.name?.split(' ')[0] || "Usuário"}
                    </span>
                    <div onClick={() => isAdmin && navigate('/admin-dashboard')} className={`text-[7px] md:text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md flex items-center gap-1 cursor-pointer ${isAdmin ? 'bg-red-600 text-white shadow-md' : isRejected ? 'bg-red-100 text-red-600 border border-red-200' : 'bg-slate-100 text-slate-400'}`}>
                       {isAdmin ? <Shield size={8} /> : isRejected ? <ShieldAlert size={8} /> : null} 
                       {isAdmin ? 'Admin' : isRejected ? 'Rejeitado' : (isAtProfessionalPanel ? 'Profissional' : 'Cliente')}
                    </div>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <MapPin size={10} className={`${isAtProfessionalPanel ? 'text-orange-400' : 'text-red-500'}`} />
                  <span className={`text-[9px] md:text-[10px] font-bold uppercase tracking-tight ${isAtProfessionalPanel ? 'text-orange-700/70' : 'text-slate-500'}`}>
                    {displayCity}
                  </span>
                </div>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="relative w-8 h-8 md:w-9 md:h-9 rounded-full bg-slate-50 flex items-center justify-center hover:bg-white transition-all shadow-sm group border border-slate-100">
                    <Bell size={16} className={`transition-colors ${unreadCount > 0 ? (isAtProfessionalPanel ? 'text-orange-600' : 'text-blue-600') : 'text-slate-400'}`} />
                    {unreadCount > 0 && (
                      <span className={`absolute -top-1 -right-1 w-4 h-4 text-white text-[8px] font-black rounded-full flex items-center justify-center border-2 border-white animate-bounce ${isAtProfessionalPanel ? 'bg-orange-600' : 'bg-red-500'}`}>
                        {unreadCount}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-80 bg-white/95 backdrop-blur-md p-2 shadow-2xl border-slate-100 rounded-[2rem] z-[200] mt-2" align="start">
                  <DropdownMenuLabel className="px-4 py-4 font-black text-slate-900 uppercase italic text-[11px] tracking-widest flex justify-between items-center border-b mb-2">Notificações</DropdownMenuLabel>
                  <div className="max-h-[350px] overflow-y-auto custom-scrollbar text-left">
                    {notifications.length > 0 ? notifications.map((n) => (
                      <DropdownMenuItem key={n.id} onClick={() => handleMarkAsRead(n)} className={`p-4 rounded-2xl mb-1 flex items-start gap-4 transition-all cursor-pointer ${n.read ? 'opacity-60' : `bg-white border-l-4 ${n.type === 'technician' ? 'border-orange-500 bg-orange-50/30' : 'border-blue-600 bg-blue-50/30'}`}`}>
                        <div className={`p-2 rounded-xl shrink-0 ${n.type === 'technician' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                          {getNotificationIcon(n.category)}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-[11px] font-black text-slate-900 leading-tight uppercase italic">{n.title}</p>
                          <p className="text-[10px] font-medium text-slate-500 mt-1 leading-snug">{n.message}</p>
                          <div className="flex items-center gap-1 mt-2 text-[8px] font-bold text-slate-400 uppercase italic text-left">
                            <Clock size={8} /> {n.createdAt?.seconds ? new Date(n.createdAt.toDate()).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Agora'}
                          </div>
                        </div>
                      </DropdownMenuItem>
                    )) : (
                      <div className="py-10 text-center uppercase font-black text-[9px] text-slate-300 italic flex flex-col items-center gap-2">
                        <BellOff size={20} />
                        Sem notificações
                      </div>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/notifications')} className="p-3 text-center justify-center font-black text-blue-600 uppercase text-[9px] italic cursor-pointer hover:bg-blue-50 rounded-b-[1.5rem]">
                    Ver histórico de notificações
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-1 justify-center">
            <img src={logoImg} alt="Socorre Aí" className="h-8 md:h-10 w-auto hidden lg:block cursor-pointer" onClick={() => navigate(isAdmin ? '/admin-dashboard' : (isAtProfessionalPanel ? '/technician-dashboard' : '/dashboard'))} />
            {hasProfessionalAccess && (
                <button onClick={() => navigate(isAtProfessionalPanel ? '/dashboard' : '/technician-dashboard')} className={`flex items-center gap-2 px-4 h-10 rounded-xl font-black text-[9px] uppercase italic transition-all shadow-lg border-b-4 ${isAtProfessionalPanel ? 'bg-blue-600 border-blue-800 text-white hover:bg-blue-700' : 'bg-orange-500 border-orange-700 text-white hover:bg-orange-600'}`}>
                    <ArrowRightLeft size={14} /> 
                    <span className="hidden sm:inline">{isAtProfessionalPanel ? 'Modo Cliente' : 'Modo Profissional'}</span>
                </button>
            )}
        </div>

        <div className="shrink-0">
          <button onClick={handleSignOut} className={`w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center transition-all active:scale-95 shadow-md ${isAtProfessionalPanel ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-orange-200' : 'bg-white text-red-600 border border-red-100 hover:bg-red-50 shadow-slate-200'}`} title="Sair">
            <Power size={18} />
          </button>
        </div>
      </div>
      <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; }` }} />
    </header>
  );
};

export default Navbar;